'use client';

import { useState, useEffect, useMemo } from 'react';
import Link from 'next/link';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Download,
  Smartphone,
  Monitor,
  QrCode,
  ChevronDown,
  Sparkles,
  ArrowRight,
} from 'lucide-react';
import { generateQRCodeUrl } from '@/lib/utils';

const features = [
  {
    icon: '💬',
    title: '在线聊天',
    description: '随时随地与同学、朋友实时沟通，支持群聊功能',
    color: 'from-blue-500 to-cyan-500',
  },
  {
    icon: '📝',
    title: '帖子社区',
    description: '分享校园生活点滴，发现有趣的校园动态',
    color: 'from-purple-500 to-pink-500',
  },
  {
    icon: '🏷️',
    title: '闲置物品',
    description: '二手交易平台，让闲置物品找到新主人',
    color: 'from-orange-500 to-yellow-500',
  },
  {
    icon: '💼',
    title: '求职招聘',
    description: '校园兼职与实习信息，助力职业发展',
    color: 'from-green-500 to-emerald-500',
  },
  {
    icon: '🎉',
    title: '组局找搭子',
    description: '约饭、游戏、运动...找到志同道合的伙伴',
    color: 'from-red-500 to-rose-500',
  },
  {
    icon: '📅',
    title: '课程表',
    description: '便捷的课程管理，让学习更有条理',
    color: 'from-indigo-500 to-blue-500',
  },
  {
    icon: '🚀',
    title: '校内创业',
    description: '校园创业扶持，发现商机与合作伙伴',
    color: 'from-violet-500 to-purple-500',
  },
  {
    icon: '🏪',
    title: '集市摊位',
    description: '线上预约线下摆摊，校园集市新体验',
    color: 'from-teal-500 to-cyan-500',
  },
];

const stats = [
  { label: '注册用户', value: '50万+' },
  { label: '覆盖高校', value: '500+' },
  { label: '日活用户', value: '10万+' },
  { label: '安全运行', value: '1000天+' },
];

export function HeroSection() {
  const [showQR, setShowQR] = useState(false);
  const [scrollY, setScrollY] = useState(0);
  const downloadUrl = process.env.NEXT_PUBLIC_DOWNLOAD_URL || '/download/app.apk';
  const qrCodeUrl = generateQRCodeUrl(downloadUrl);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Dynamic Background */}
      <div className="absolute inset-0">
        {/* Gradient orbs */}
        <div
          className="absolute top-1/4 -left-32 w-96 h-96 bg-gradient-to-r from-primary/30 to-emerald-500/20 rounded-full blur-3xl animate-pulse"
          style={{ transform: `translateY(${scrollY * 0.3}px)` }}
        />
        <div
          className="absolute bottom-1/4 -right-32 w-96 h-96 bg-gradient-to-l from-emerald-500/30 to-cyan-500/20 rounded-full blur-3xl animate-pulse"
          style={{ transform: `translateY(${scrollY * -0.2}px)`, animationDelay: '1s' }}
        />
        <div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-gradient-to-r from-primary/10 via-emerald-500/10 to-cyan-500/10 rounded-full blur-3xl"
          style={{ transform: `translateX(-50%) translateY(${scrollY * 0.1}px)` }}
        />
        
        {/* Grid pattern */}
        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.03)_1px,transparent_1px)] bg-[size:70px_70px] [mask-image:radial-gradient(ellipse_80%_80%_at_50%_50%,#000_70%,transparent_100%)]" />
      </div>

      {/* Floating particles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {useMemo(() => 
          Array.from({ length: 20 }, (_, i) => ({
            left: `${(i * 17 + 13) % 100}%`,
            top: `${(i * 23 + 7) % 100}%`,
            delay: `${(i * 0.3) % 5}s`,
            duration: `${3 + (i * 0.4) % 4}s`,
          })).map((style, i) => (
            <div
              key={i}
              className="absolute w-1 h-1 bg-primary/50 rounded-full animate-float"
              style={{
                left: style.left,
                top: style.top,
                animationDelay: style.delay,
                animationDuration: style.duration,
              }}
            />
          )), [])}
      </div>

      <div className="container mx-auto px-4 py-20 relative z-10">
        <div className="text-center max-w-5xl mx-auto">
          {/* Badge */}
          <div className="mb-8 animate-fade-in-up">
            <Badge variant="secondary" className="px-6 py-2 text-base bg-primary/10 text-primary border-primary/20 hover:bg-primary/20">
              <Sparkles className="w-4 h-4 mr-2 animate-spin-slow" />
              v1.0.3 全新版本
            </Badge>
          </div>

          {/* Main Title */}
          <h1
            className="text-5xl md:text-7xl lg:text-8xl font-bold mb-6 tracking-tight animate-fade-in-up"
            style={{ animationDelay: '0.1s' }}
          >
            <span className="bg-gradient-to-r from-foreground via-foreground to-muted-foreground bg-clip-text text-transparent">
              校园知书
            </span>
          </h1>

          <p
            className="text-xl md:text-2xl lg:text-3xl text-muted-foreground mb-8 font-light animate-fade-in-up"
            style={{ animationDelay: '0.2s' }}
          >
            大学生专属校园服务社区
          </p>

          <p
            className="text-lg text-muted-foreground/80 max-w-2xl mx-auto mb-12 leading-relaxed animate-fade-in-up"
            style={{ animationDelay: '0.3s' }}
          >
            专为大学生打造的校园服务社区APP，集在线聊天、帖子、闲置物品、求职招聘、组局等功能于一体，让校园生活更便捷、更精彩。
          </p>

          {/* Stats */}
          <div
            className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-12 animate-fade-in-up"
            style={{ animationDelay: '0.4s' }}
          >
            {stats.map((stat, i) => (
              <div key={stat.label} className="group">
                <div className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-primary to-emerald-500 bg-clip-text text-transparent group-hover:scale-110 transition-transform duration-300">
                  {stat.value}
                </div>
                <div className="text-sm text-muted-foreground mt-1">{stat.label}</div>
              </div>
            ))}
          </div>

          {/* Download Buttons */}
          <div
            className="flex flex-wrap justify-center gap-4 mb-8 animate-fade-in-up"
            style={{ animationDelay: '0.5s' }}
          >
            <Button
              size="lg"
              asChild
              className="h-14 px-8 text-lg bg-gradient-to-r from-primary to-emerald-600 hover:from-primary/90 hover:to-emerald-600/90 shadow-lg shadow-primary/25 hover:shadow-primary/40 transition-all duration-300 group"
            >
              <a href={downloadUrl} download="校园知书.apk" className="gap-2">
                <Download className="h-5 w-5 group-hover:animate-bounce" />
                Android 下载
              </a>
            </Button>

            <Dialog open={showQR} onOpenChange={setShowQR}>
              <DialogTrigger asChild>
                <Button
                  size="lg"
                  variant="outline"
                  className="h-14 px-8 text-lg border-2 hover:bg-primary/5 transition-all duration-300 group"
                >
                  <QrCode className="h-5 w-5 mr-2 group-hover:scale-110 transition-transform" />
                  扫码下载
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle className="text-center text-xl">扫码下载校园知书</DialogTitle>
                </DialogHeader>
                <div className="flex flex-col items-center gap-4 py-4">
                  <div className="p-4 bg-white rounded-2xl shadow-xl">
                    <img src={qrCodeUrl} alt="下载二维码" className="w-64 h-64" />
                  </div>
                  <p className="text-sm text-muted-foreground">
                    使用微信或其他扫码工具扫描下载
                  </p>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          {/* Platform info */}
          <div
            className="flex items-center justify-center gap-6 text-sm text-muted-foreground animate-fade-in-up"
            style={{ animationDelay: '0.6s' }}
          >
            <div className="flex items-center gap-2">
              <Smartphone className="h-4 w-4" />
              <span>iOS / Android</span>
            </div>
            <div className="w-px h-4 bg-border" />
            <div className="flex items-center gap-2">
              <Monitor className="h-4 w-4" />
              <span>Web 版即将上线</span>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <ChevronDown className="h-8 w-8 text-muted-foreground" />
        </div>
      </div>
    </section>
  );
}

export function FeaturesSection() {
  return (
    <section className="py-32 relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0 bg-gradient-to-b from-background via-primary/5 to-background" />
      
      <div className="container mx-auto px-4 relative z-10">
        <div className="text-center mb-20">
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
            <span className="bg-gradient-to-r from-foreground to-foreground/50 bg-clip-text text-transparent">
              丰富的校园功能
            </span>
          </h2>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            专为大学生设计的校园服务，一站式满足你的校园生活需求
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {features.map((feature, index) => (
            <div
              key={feature.title}
              className="group relative"
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              {/* Glow effect */}
              <div className={`absolute -inset-0.5 bg-gradient-to-r ${feature.color} rounded-2xl opacity-0 group-hover:opacity-20 blur-lg transition-opacity duration-500`} />
              
              <div className="relative h-full bg-background border border-border rounded-2xl p-6 group-hover:border-primary/30 transition-all duration-300 group-hover:-translate-y-2">
                {/* Icon */}
                <div className={`w-14 h-14 rounded-xl bg-gradient-to-r ${feature.color} flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg`}>
                  <span className="text-2xl">{feature.icon}</span>
                </div>
                
                <h3 className="text-xl font-semibold mb-3 group-hover:text-primary transition-colors">
                  {feature.title}
                </h3>
                <p className="text-muted-foreground leading-relaxed">
                  {feature.description}
                </p>
                
                {/* Arrow */}
                <div className="mt-4 flex items-center text-primary opacity-0 group-hover:opacity-100 transition-opacity">
                  <span className="text-sm font-medium">了解更多</span>
                  <ArrowRight className="h-4 w-4 ml-1 group-hover:translate-x-1 transition-transform" />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function DownloadSection() {
  const [showQR, setShowQR] = useState(false);
  const downloadUrl = process.env.NEXT_PUBLIC_DOWNLOAD_URL || '/download/app.apk';
  const qrCodeUrl = generateQRCodeUrl(downloadUrl);

  return (
    <section className="py-32 relative overflow-hidden">
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-r from-primary/10 via-emerald-500/10 to-cyan-500/10" />
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary/50 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary/50 to-transparent" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-6xl mx-auto">
          <div className="relative bg-gradient-to-br from-primary/5 via-background to-emerald-500/5 rounded-[3rem] p-2 border border-primary/10">
            <div className="bg-background rounded-[2.5rem] overflow-hidden">
              <div className="grid md:grid-cols-2">
                {/* Left - Content */}
                <div className="p-12 lg:p-16 flex flex-col justify-center">
                  <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 rounded-full text-primary text-sm font-medium mb-6 w-fit">
                    <Download className="h-4 w-4" />
                    立即体验
                  </div>

                  <h2 className="text-4xl md:text-5xl font-bold mb-6">
                    <span className="bg-gradient-to-r from-foreground to-foreground/70 bg-clip-text text-transparent">
                      开启你的
                    </span>
                    <br />
                    <span className="bg-gradient-to-r from-primary to-emerald-500 bg-clip-text text-transparent">
                      校园新生活
                    </span>
                  </h2>

                  <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
                    加入校园知书，与百万大学生一起，发现校园生活的无限可能。
                  </p>

                  <div className="flex flex-wrap gap-4">
                    <Button
                      size="lg"
                      asChild
                      className="h-12 px-6 bg-gradient-to-r from-primary to-emerald-600 hover:shadow-lg hover:shadow-primary/25 transition-all"
                    >
                      <a href={downloadUrl} download="校园知书.apk">
                        <Download className="h-5 w-5 mr-2" />
                        Android APK
                      </a>
                    </Button>
                    <Button
                      size="lg"
                      variant="outline"
                      className="h-12 px-6"
                      onClick={() => setShowQR(true)}
                    >
                      <QrCode className="h-5 w-5 mr-2" />
                      扫码下载
                    </Button>
                  </div>
                </div>

                {/* Right - QR Code */}
                <div className="p-12 lg:p-16 flex items-center justify-center bg-gradient-to-br from-primary/5 to-emerald-500/5">
                  <div className="relative">
                    {/* Glow */}
                    <div className="absolute inset-0 bg-gradient-to-r from-primary to-emerald-500 rounded-3xl blur-2xl opacity-20" />
                    
                    <div className="relative bg-white p-6 rounded-3xl shadow-2xl">
                      <img
                        src={qrCodeUrl}
                        alt="下载二维码"
                        className="w-72 h-72"
                      />
                      <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 px-6 py-2 bg-primary text-primary-foreground rounded-full text-sm font-medium whitespace-nowrap">
                        扫码下载
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export function CTA() {
  return (
    <section className="py-32 relative overflow-hidden">
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-br from-foreground via-foreground/95 to-foreground" />
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmZmZmYiIGZpbGwtb3BhY2l0eT0iMC4wNSI+PGNpcmNsZSBjeD0iMzAiIGN5PSIzMCIgcj0iMiIvPjwvZz48L2c+PC9zdmc+')] opacity-50" />
      </div>

      <div className="container mx-auto px-4 relative z-10 text-center">
        <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-primary-foreground mb-6">
          准备好开启校园新生活了吗？
        </h2>
        <p className="text-xl text-primary-foreground/70 max-w-2xl mx-auto mb-10">
          校园知书陪伴你的大学时光，让每一次连接都有价值。立即下载，与百万大学生一起，发现校园生活的无限可能。
        </p>
        <div className="flex flex-wrap justify-center gap-4">
          <Button
            size="lg"
            asChild
            className="h-14 px-10 bg-white text-foreground hover:bg-white/90 shadow-xl"
          >
            <Link href="/about">
              了解更多
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
          </Button>
          <Button
            size="lg"
            variant="outline"
            className="h-14 px-10 border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10"
            asChild
          >
            <Link href="/news">
              查看动态
            </Link>
          </Button>
        </div>
      </div>
    </section>
  );
}
